import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Save,
    Coins,
    Gift,
    Plus,
    Trash2,
    Pencil,
    Image as ImageIcon,
    ExternalLink,
    Info,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useConfirm } from "../../components/ConfirmDialog";

export default function AdminRewards() {
    const [settings, setSettings] = useState(null);
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [editing, setEditing] = useState(null); // partner being edited or "new"
    const confirm = useConfirm();

    const reload = async () => {
        setLoading(true);
        try {
            const [s, p] = await Promise.all([
                api.get("/rewards/settings"),
                api.get("/rewards/partners"),
            ]);
            setSettings(s.data);
            setPartners(p.data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        reload();
    }, []);

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            const { data } = await api.put("/admin/rewards/settings", settings);
            setSettings(data);
            toast.success("Paramètres enregistrés");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        } finally {
            setSavingSettings(false);
        }
    };

    const removePartner = async (p) => {
        const ok = await confirm({
            title: "Supprimer cet avantage ?",
            description: `L'avantage "${p.name}" ne sera plus disponible. Action définitive.`,
            confirmLabel: "Supprimer",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/admin/rewards/partners/${p.reward_id}`);
            setPartners((prev) => prev.filter((x) => x.reward_id !== p.reward_id));
            toast.success("Avantage supprimé");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        }
    };

    const onPartnerSaved = (p, isNew) => {
        if (isNew) setPartners((prev) => [p, ...prev]);
        else setPartners((prev) => prev.map((x) => (x.reward_id === p.reward_id ? p : x)));
    };

    if (loading || !settings) return <div className="h-32 glass-card rounded-3xl animate-pulse" />;

    return (
        <div className="space-y-6" data-testid="admin-rewards">
            <div>
                <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                    Conversion des points
                </p>
                <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
                    <Gift className="w-6 h-6 text-shariti-orange-light" />
                    Récompenses & valeur des points
                </h2>
                <p className="text-sm text-white/60 mt-1 flex items-start gap-2">
                    <Info className="w-3.5 h-3.5 mt-1 text-shariti-orange-light shrink-0" />
                    Configure ici comment les points peuvent être convertis (en euros ou
                    en avantages partenaires). Tout reste désactivable à tout moment, et
                    aucun paiement n'est automatique côté plateforme : c'est uniquement
                    un affichage côté utilisateur (MVP).
                </p>
            </div>

            {/* Cash conversion */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-3xl p-5 lg:p-6 space-y-4"
                data-testid="rewards-cash-card"
            >
                <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-shariti-yellow/15 flex items-center justify-center shrink-0">
                        <Coins className="w-5 h-5 text-shariti-yellow" />
                    </div>
                    <div className="flex-1">
                        <p className="font-extrabold text-white">Option 1 — Conversion financière</p>
                        <p className="text-xs text-white/55 font-light mt-0.5">
                            Affiche aux utilisateurs une estimation en euros de leurs points cumulés.
                        </p>
                    </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <ToggleRow
                        label="Afficher l'estimation côté utilisateur"
                        description="Quand activé, les apprenants voient '≈ X €' à côté de leur total."
                        checked={settings.cash_visible}
                        onChange={(v) => setSettings({ ...settings, cash_visible: v })}
                        testid="cash-visible"
                    />
                    <ToggleRow
                        label="Engagement de payout (réservé phase 2)"
                        description="Si activé, les points pourront être réclamés. Laisse OFF pour le MVP."
                        checked={settings.cash_enabled}
                        onChange={(v) => setSettings({ ...settings, cash_enabled: v })}
                        testid="cash-enabled"
                    />
                </div>
                <div>
                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                        Taux de conversion (euros par point)
                    </label>
                    <input
                        type="number"
                        step="0.0001"
                        min="0"
                        value={settings.cash_rate_eur_per_point}
                        onChange={(e) =>
                            setSettings({
                                ...settings,
                                cash_rate_eur_per_point: parseFloat(e.target.value) || 0,
                            })
                        }
                        data-testid="cash-rate-input"
                        className="w-full md:w-64 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white outline-none focus:border-shariti-orange/60"
                    />
                    <p className="text-xs text-white/40 mt-1">
                        Exemple : 0,001 € → 1 000 pts = 1 €.
                    </p>
                </div>
                <ToggleRow
                    label="Activer la liste des avantages partenaires"
                    description="Permet aux utilisateurs de voir le catalogue de partenaires (lecture seule pour le MVP)."
                    checked={settings.partners_enabled}
                    onChange={(v) => setSettings({ ...settings, partners_enabled: v })}
                    testid="partners-enabled"
                />
                <div>
                    <button
                        onClick={saveSettings}
                        disabled={savingSettings}
                        data-testid="rewards-settings-save"
                        className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                    >
                        <Save className="w-4 h-4" />
                        {savingSettings ? "Sauvegarde..." : "Enregistrer"}
                    </button>
                </div>
            </motion.div>

            {/* Partners */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-extrabold text-white flex items-center gap-2">
                        <Gift className="w-5 h-5 text-shariti-orange-light" />
                        Avantages partenaires ({partners.length})
                    </h3>
                    <button
                        onClick={() => setEditing("new")}
                        data-testid="rewards-add-partner"
                        className="inline-flex items-center gap-1.5 bg-shariti-orange text-white font-extrabold text-xs uppercase tracking-widest px-3 py-2 rounded-xl hover:bg-shariti-orange-light transition-all"
                    >
                        <Plus className="w-3.5 h-3.5" /> Ajouter
                    </button>
                </div>
                {partners.length === 0 ? (
                    <div className="glass-card rounded-3xl p-8 text-center">
                        <Gift className="w-10 h-10 text-white/30 mx-auto mb-3" />
                        <p className="text-white/60">
                            Aucun avantage partenaire pour l'instant. Crée-en un pour le proposer
                            à tes recruteurs.
                        </p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {partners.map((p) => (
                            <div
                                key={p.reward_id}
                                className="glass-card rounded-2xl p-4 space-y-3"
                                data-testid={`partner-${p.reward_id}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                                        {p.image_url ? (
                                            <img
                                                src={p.image_url}
                                                alt={p.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <ImageIcon className="w-5 h-5 text-white/30" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-extrabold text-white truncate">
                                            {p.name}
                                        </p>
                                        <p className="text-xs text-shariti-orange-light font-bold">
                                            {p.cost_points.toLocaleString("fr-FR")} pts
                                        </p>
                                    </div>
                                    <span
                                        className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${
                                            p.is_active
                                                ? "bg-emerald-500/20 text-emerald-400"
                                                : "bg-white/5 text-white/40"
                                        }`}
                                    >
                                        {p.is_active ? "Actif" : "Inactif"}
                                    </span>
                                </div>
                                {p.description && (
                                    <p className="text-xs text-white/55 line-clamp-2">
                                        {p.description}
                                    </p>
                                )}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setEditing(p)}
                                        data-testid={`partner-edit-${p.reward_id}`}
                                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 font-bold text-xs px-3 py-2 rounded-xl transition-all"
                                    >
                                        <Pencil className="w-3.5 h-3.5" /> Éditer
                                    </button>
                                    <button
                                        onClick={() => removePartner(p)}
                                        data-testid={`partner-delete-${p.reward_id}`}
                                        className="inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-white/60 hover:text-red-400 font-bold text-xs px-3 py-2 rounded-xl transition-all"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {editing && (
                <PartnerEditor
                    partner={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={(p, isNew) => {
                        onPartnerSaved(p, isNew);
                        setEditing(null);
                    }}
                />
            )}
        </div>
    );
}

function ToggleRow({ label, description, checked, onChange, testid }) {
    return (
        <label
            className="flex items-start gap-3 p-3 rounded-2xl bg-white/5 cursor-pointer hover:bg-white/[0.07] transition-all"
            data-testid={`toggle-${testid}`}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-1 w-4 h-4 accent-shariti-orange shrink-0"
                data-testid={`toggle-input-${testid}`}
            />
            <div>
                <p className="text-sm font-bold text-white">{label}</p>
                <p className="text-xs text-white/55 font-light mt-0.5">{description}</p>
            </div>
        </label>
    );
}

function PartnerEditor({ partner, onClose, onSaved }) {
    const isNew = !partner;
    const [form, setForm] = useState(
        partner || {
            name: "",
            description: "",
            cost_points: 1000,
            image_url: "",
            redeem_url: "",
            is_active: true,
        },
    );
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isNew) {
                const { data } = await api.post("/admin/rewards/partners", form);
                onSaved(data, true);
                toast.success("Avantage créé");
            } else {
                const { data } = await api.put(
                    `/admin/rewards/partners/${partner.reward_id}`,
                    form,
                );
                onSaved(data, false);
                toast.success("Avantage modifié");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-shariti-black/85 backdrop-blur flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="partner-editor"
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg glass-card rounded-3xl p-6 my-auto"
            >
                <h3 className="text-xl font-extrabold text-white mb-4">
                    {isNew ? "Nouvel avantage" : "Modifier l'avantage"}
                </h3>
                <div className="space-y-3">
                    <Field label="Nom" testid="partner-name">
                        <input
                            type="text"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white outline-none focus:border-shariti-orange/60"
                        />
                    </Field>
                    <Field label="Description" testid="partner-description">
                        <textarea
                            rows={2}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white outline-none focus:border-shariti-orange/60 resize-none"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Coût en points" testid="partner-cost">
                            <input
                                type="number"
                                min={0}
                                required
                                value={form.cost_points}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        cost_points: parseInt(e.target.value, 10) || 0,
                                    })
                                }
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white outline-none focus:border-shariti-orange/60"
                            />
                        </Field>
                        <Field label="Statut">
                            <label className="flex items-center gap-2 cursor-pointer h-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl">
                                <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(e) =>
                                        setForm({ ...form, is_active: e.target.checked })
                                    }
                                    className="w-4 h-4 accent-shariti-orange"
                                    data-testid="partner-active"
                                />
                                <span className="text-sm text-white/80 font-bold">Actif</span>
                            </label>
                        </Field>
                    </div>
                    <Field label="URL de l'image (optionnel)" testid="partner-image">
                        <input
                            type="url"
                            value={form.image_url}
                            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                            placeholder="https://…"
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                        />
                    </Field>
                    <Field label="URL de redirection (optionnel)" testid="partner-redeem">
                        <input
                            type="url"
                            value={form.redeem_url}
                            onChange={(e) => setForm({ ...form, redeem_url: e.target.value })}
                            placeholder="https://…"
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                        />
                    </Field>
                </div>
                <div className="flex gap-2 mt-5">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 glass-card rounded-2xl py-3 text-white font-bold hover:bg-white/5 transition-all"
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="partner-save"
                        className="flex-1 bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                    >
                        {saving ? "Sauvegarde..." : "Enregistrer"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                {label}
            </label>
            {children}
        </div>
    );
}
